import { z } from 'zod';
import { readinessStateSchema } from '../readiness/vocabulary.js';
/**
 * A user's own display preferences, kept with their sign-in (ADR-019, #476).
 *
 * One document per user, a section per list screen. Today the only screen is the roster; adding
 * another is a name in `PREFERENCE_SCREENS` and a filter schema for it (ADR-019 §2).
 *
 * WHAT THE SCHEMA REFUSES. Every object below is `.strict()`, so a field for the search box, a
 * person's id, name, email, phone or a user id is refused, not ignored. `compliance`, `statuses`
 * and `phones` accept only their closed lists on write. A service area and a saved filter's name
 * are the only free text, limited to 100 and 40 characters with no control characters.
 *
 * WHAT THE SCHEMA CANNOT REFUSE, AND THE SERVER DOES (Julio, "AR strict", 2026-10-05). While a service
 * area is typed text (ADR-019 §1, until ADR-018), this schema accepts any short text in `areas`. The
 * API therefore refuses, on every write that carries areas, any area that is not the no-area marker
 * or a service area held by someone on the roster right now (`areasInUse`), so a name, an email or a
 * phone typed as an area is refused. The rule changes to place ids when ADR-018 ships. Only the areas
 * in the request are checked: a stored area that has fallen out of use never blocks another write.
 * A saved filter's name is the user's own words and is shown only to them (ADR-019 §1).
 */
/** The closed list of screens that may keep filters. A screen not named here has no route. */
export const PREFERENCE_SCREENS = ['roster'];
export const preferenceScreenSchema = z.enum(PREFERENCE_SCREENS);
/**
 * Limits. PLANNER DEFAULTS, not Julio's decisions (#476): change them by amending ADR-019.
 */
export const MAX_SAVED_FILTERS = 20;
/**
 * A hard bound on the stored list, readable or not, so the document cannot grow without end. Only
 * saved filters this build can SHOW count toward the 20 (ADR-019 §5); this is the backstop above it.
 */
export const MAX_STORED_SAVED_FILTERS = 40;
export const SAVED_FILTER_NAME_MAX = 40;
/** A service area is typed text today (ADR-018 converts it to a place id later). */
export const SERVICE_AREA_MAX = 100;
/** The most areas one filter may name. A bound on the document, not a product rule. */
export const MAX_FILTER_AREAS = 20;
/** Twelve months, in seconds. Each write sets the document's `ttl` to this (ADR-019 Q4). */
export const PREFERENCES_TTL_SECONDS = 365 * 24 * 60 * 60;
/**
 * The roster's "no service area" choice. It holds a NUL, so it can never equal text a person
 * typed. Mirrors `NO_AREA` in the roster's `staff.ts` (PR #485); the web part imports this one.
 */
export const ROSTER_NO_AREA = '\u0000none';
/** C0 and C1 control characters, by code, so no control character sits in a regular expression. */
const hasControlCharacter = (text) => [...text].some((ch) => {
    const code = ch.codePointAt(0) ?? 0;
    return code <= 0x1f || (code >= 0x7f && code <= 0x9f);
});
const serviceAreaText = z
    .string()
    .trim()
    .min(1)
    .max(SERVICE_AREA_MAX)
    .refine((text) => !hasControlCharacter(text), 'A service area holds no control characters.');
const serviceAreaChoice = z.union([z.literal(ROSTER_NO_AREA), serviceAreaText]);
export const ROSTER_COMPLIANCE_CHOICES = ['not-tracked', ...readinessStateSchema.options];
export const ROSTER_STATUS_CHOICES = ['active', 'inactive'];
export const ROSTER_PHONE_CHOICES = ['has', 'none'];
/**
 * The roster's filter, with the choice of each list left as a parameter so the WRITE schema
 * (closed lists) and the READ schema (any short text) are one shape, not two.
 *
 * Reading is wide on the choices: a status renamed in code must reach the roster as the old text
 * so the screen can say "X no longer exists" (ADR-019 §5), not vanish with the whole filter.
 */
function rosterFilterShape(compliance, statuses, phones) {
    return z
        .object({
        areas: z.array(serviceAreaChoice).max(MAX_FILTER_AREAS),
        compliance: z.array(compliance).max(ROSTER_COMPLIANCE_CHOICES.length),
        statuses: z.array(statuses).max(ROSTER_STATUS_CHOICES.length),
        phones: z.array(phones).max(ROSTER_PHONE_CHOICES.length),
    })
        .strict();
}
const choiceText = z.string().min(1).max(SERVICE_AREA_MAX);
/** What a client may write. Closed-list choices only; no search text. */
export const rosterFilterSchema = rosterFilterShape(z.enum(ROSTER_COMPLIANCE_CHOICES), z.enum(ROSTER_STATUS_CHOICES), z.enum(ROSTER_PHONE_CHOICES));
/** What may be read back: the same fields, with each choice any short text. */
export const storedRosterFilterSchema = rosterFilterShape(choiceText, choiceText, choiceText);
export const noRosterFilterPreference = { areas: [], compliance: [], statuses: [], phones: [] };
/** A name 1 to 40 characters after trimming, with no control characters. */
export const savedFilterNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(SAVED_FILTER_NAME_MAX)
    .refine((name) => !hasControlCharacter(name), 'A name holds no control characters.');
/** Names repeat when they match ignoring capitals, once trimmed. */
export const sameSavedFilterName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
const savedFilterBase = {
    id: z.string().uuid(),
    name: savedFilterNameSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
};
export const savedRosterFilterSchema = z.object({ ...savedFilterBase, filters: storedRosterFilterSchema }).strict();
const uniqueNames = (saved) => saved.every((item, i) => saved.findIndex((other) => sameSavedFilterName(other.name, item.name)) === i);
/** One screen's section: the last-used filter and the named ones. */
export const rosterPreferencesSchema = z
    .object({
    last: storedRosterFilterSchema.nullable(),
    saved: z.array(savedRosterFilterSchema).max(MAX_SAVED_FILTERS).refine(uniqueNames, 'A saved filter name is used once.'),
})
    .strict();
export const emptyRosterPreferences = () => ({ last: null, saved: [] });
/**
 * What the API returns for GET /api/preferences, and what the web app holds.
 *
 * `unreadableSaved` counts the stored saved filters this build cannot show (written by another
 * version, or beyond what is shown). A COUNT ONLY: nothing from their content is reported.
 */
export const rosterViewSchema = rosterPreferencesSchema.extend({ unreadableSaved: z.number().int().nonnegative() }).strict();
export const preferencesViewSchema = z.object({ screens: z.object({ roster: rosterViewSchema }).strict() }).strict();
/** The stored document: `userprefs:{userId}`, partitioned on `/userId`, never indexed. */
export const preferencesDocumentSchema = z
    .object({
    id: z.string().startsWith('userprefs:'),
    userId: z.string().min(1),
    docType: z.literal('user-preferences'),
    screens: z.object({ roster: rosterPreferencesSchema }).strict(),
    /** Seconds. Always PREFERENCES_TTL_SECONDS: each write moves expiry 12 months out. */
    ttl: z.number().int().positive(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const preferencesDocumentId = (userId) => `userprefs:${userId}`;
/** A user id that can safely be part of a Cosmos id (the SWA value is a hex string). */
export const isUsablePreferencesUserId = (userId) => /^[A-Za-z0-9_-]{1,128}$/.test(userId);
/**
 * Which stored saved filters this build can SHOW, as positions in the stored list. One rule used by
 * the read and by "remove the ones I cannot see", so the two can never disagree: a saved filter is
 * shown if it parses, does not repeat an earlier shown name (ignoring capitals), and fits the 20.
 */
export function shownSavedFilters(storedSaved) {
    const shown = [];
    const shownIndexes = [];
    if (!Array.isArray(storedSaved))
        return { shown, shownIndexes };
    for (const [index, item] of storedSaved.entries()) {
        const parsed = savedRosterFilterSchema.safeParse(item);
        if (!parsed.success)
            continue;
        if (shown.some((kept) => sameSavedFilterName(kept.name, parsed.data.name)))
            continue;
        if (shown.length >= MAX_SAVED_FILTERS)
            break;
        shown.push(parsed.data);
        shownIndexes.push(index);
    }
    return { shown, shownIndexes };
}
/**
 * Reads a stored document without ever throwing (ADR-019 §5, the #348 pattern). A part that does
 * not parse is treated as empty, and one bad saved filter does not take the others with it.
 */
export function readPreferencesView(raw) {
    const roster = raw?.screens?.roster;
    const section = typeof roster === 'object' && roster !== null ? roster : {};
    const last = storedRosterFilterSchema.safeParse(section.last);
    const { shown } = shownSavedFilters(section.saved);
    const stored = Array.isArray(section.saved) ? section.saved.length : 0;
    return { screens: { roster: { last: last.success ? last.data : null, saved: shown, unreadableSaved: stored - shown.length } } };
}
/** A saved filter's id as the server makes it. DELETE by id refuses any other shape. */
export const SAVED_FILTER_ID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The literal last segment of the route that removes the saved filters this build cannot show. */
export const UNREADABLE_SAVED_SEGMENT = 'unreadable';
/* ---- Request bodies. Strict, so a search value, a person id or a user id is refused. ---- */
export const setLastFiltersRequestSchema = z.object({ filters: rosterFilterSchema }).strict();
export const createSavedFilterRequestSchema = z.object({ name: savedFilterNameSchema, filters: rosterFilterSchema }).strict();
export const renameSavedFilterRequestSchema = z.object({ name: savedFilterNameSchema }).strict();
/** What POST .../saved answers: the filter just saved, and the user's whole view as it now stands. */
export const createSavedFilterResponseSchema = z.object({ saved: savedRosterFilterSchema, view: preferencesViewSchema }).strict();
export const sameServiceArea = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
export function areasInUse(people) {
    const seen = [];
    for (const person of people) {
        if (person.lifecycle !== 'active' && person.lifecycle !== 'inactive')
            continue;
        const area = person.assignment?.serviceArea?.trim();
        if (area === undefined || area === '')
            continue;
        if (!seen.some((kept) => sameServiceArea(kept, area)))
            seen.push(area);
    }
    return seen.sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=index.js.map